import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  Button,
  ButtonGroup,
  Card,
  CardContent,
  IconButton,
} from "@mui/material";
import {
  LocalShipping as LocalShippingIcon,
  CalendarToday as CalendarIcon,
} from "@mui/icons-material";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
} from "recharts";
import TotalInventory from "../../globalStyles/icons/total-inventory";
import CustomTypography from "../../globalStyles/fonts"; // Make sure this import path is correct
import CalendarPicker from "../calendar-picker/CalendarPicker";
import { useNavigate } from "react-router-dom";
import { useDarkMode } from "../context/darkModeContext";

const DashboardOrders = ({
  totalOrders = 0,
  totalDelivery = 0,
  totalInventory = 0,
  newOrders = 0,
  onChangeSelectedRange = () => {},
  orderRangeStats = {
    total: 0,
    intervals: [],
    range: "Today",
  },
  setSelectedDate,
  selectedDate = new Date(),
  enableCalendar = false,
}) => {
  const [selectedFilter, setSelectedFilter] = useState("Year");
  const [isInitialized, setIsInitialized] = useState(false);
  const calendarRef = useRef(null);
  const navigate = useNavigate();
  const { colors } = useDarkMode();

  // Load Month data initially on component mount (only once)
  useEffect(() => {
    if (!isInitialized) {
      onChangeSelectedRange("Year");
      setIsInitialized(true);
    }
  }, []);

  // (calendar behavior moved to DashboardCalendar component)

  const SidebarCard = ({ icon, value, label, color, onClick }) => (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 2,
        p: "10px 0",
        backgroundColor: "transparent",
        borderRadius: 0,
        mb: 0,
        boxShadow: "none",
      }}
      onClick={onClick ? () => onClick() : () => {}}
      style={{ cursor: onClick ? "pointer" : "default" }}
    >
      <Box
        sx={{
          width: 40,
          height: 40,
          borderRadius: "50%",
          backgroundColor: color,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
        }}
      >
        {icon}
      </Box>
      <Box sx={{ textAlign: "left", width: "100%" }}>
        <CustomTypography
          variant="body2"
          sx={{
            color: colors.textTertiary,
            fontSize: 11, // was 13
            mb: 0.5,
          }}
        >
          {label}
        </CustomTypography>
        <CustomTypography
          variant="h6"
          sx={{
            fontSize: 16, // was 18
            color: color,
            lineHeight: 1,
          }}
        >
          {value.toLocaleString()}
        </CustomTypography>
      </Box>
    </Box>
  );

  return (
    <Box
      sx={{
        background: colors.bgPrimary,
        display: "flex",
        justifyContent: "center",
        alignItems: "flex-start",
        width: "100%",
      }}
    >
      <Box
        sx={{
          background: colors.bgPaper,
          borderRadius: 3,
          border: `1px solid ${colors.borderLight}`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.04)",
          width: "100%",
          minHeight: 400,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
        }}
      >
        {/* Header Section */}
        <Box sx={{ display: "flex", flexDirection: "row" }}>
          {/* Reports Header (same width as sidebar) */}
          {/* <Box
            sx={{
              display: "flex",
              alignItems: "center",
              borderBottom: `1px solid ${colors.border}`,
              px: 3,
              py: 2,
              gap: 2,
              minHeight: 56,
              background: colors.bgPaper,
              boxSizing: "border-box",
              width: 160,
            }}
          >
            <CustomTypography
              variant="h6"
              sx={{
                fontWeight: 700,
                color: "#1A1A1A",
                fontSize: 16,
                letterSpacing: 0.5,
                minWidth: 90,
              }}
            >
              REPORTS
            </CustomTypography>
          </Box> */}

          {/* Filters and Date (rest of header) */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              alignItems: "center",
              borderBottom: `1px solid ${colors.border}`,
              px: 1,
              py: 2,
              gap: 2,
              minHeight: 56,
              background: colors.bgPaper,
              boxSizing: "border-box",
              overflow: "auto",
              maxWidth: "100%", // Adjust to fill remaining space
              // scrollbarWidth: "none", // Firefox
              // "&::-webkit-scrollbar": { display: "none" },
              justifyContent: "space-between",
            }}
          >
            <Box
              sx={{
                border: `1px solid ${colors.border}`,
                borderRadius: 1,
                px: 1.5,
                py: 0.5,
                background: colors.bgSecondary,
                minWidth: 110,
                mr: 2,
              }}
            >
              <CustomTypography
                variant="body2"
                sx={{ color: colors.textSecondary, fontSize: 12 }}
              >
                Total Order
              </CustomTypography>
            </Box>
            <ButtonGroup variant="outlined" size="small">
              {["Today", "Yesterday", "Week", "Month", "Quarter", "Year"].map(
                (filter) => (
                  <Button
                    key={filter}
                    variant={
                      selectedFilter === filter ? "contained" : "outlined"
                    }
                    onClick={() => {
                      onChangeSelectedRange(filter);
                      setSelectedFilter(filter);
                    }}
                    sx={{
                      backgroundColor:
                        selectedFilter === filter ? "#e3f2fd" : "transparent",
                      color:
                        selectedFilter === filter
                          ? "#1976d2"
                          : "var(--text-secondary)",
                      borderColor: "var(--border-color)",
                      "&:hover": {
                        backgroundColor:
                          selectedFilter === filter ? "#e3f2fd" : "#f5f5f5",
                      },
                      fontWeight: 500,
                      fontSize: 12, // was 14
                      minWidth: 80,
                    }}
                  >
                    {filter}
                  </Button>
                ),
              )}
            </ButtonGroup>
            {/* <Box sx={{ flex: 1 }} /> */}
            {/* Calendar Functionality */}
            {/*   <Box
              sx={{
                display: "flex",
                alignItems: "center",
                border: `1px solid ${colors.borderLight}`,
                borderRadius: 2,
                px: 1.5,
                py: 0.5,
                background: colors.bgPaper,
                fontSize: 14,
                color: colors.textSecondary,
                gap: 1,
                cursor: "pointer",
              }}
              ref={calendarRef}
              onClick={() => setShowCalendar((prev) => !prev)}
            >
              <CalendarMonthOutlinedIcon
                sx={{ fontSize: 18, color: "#BDBDBD" }}
              />
              <span style={{ fontSize: 12, color: "#888" }}>
                {selectedDate
                  ? selectedDate?.toLocaleDateString("en-GB")
                  : dayjs().format("DD/MM/YYYY")}
              </span>
            </Box>
            <Popper
              open={showCalendar}
              anchorEl={calendarRef.current}
              placement="bottom-start"
              disablePortal={false}
              modifiers={[
                {
                  name: "offset",
                  options: {
                    offset: [0, 4],
                  },
                },
              ]}
              sx={{ zIndex: 1300 }}
            >
              <Box
                ref={calendarContainerRef}
                sx={{
                  backgroundColor: "white",
                  borderRadius: 2,
                  boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
                  p: 2,
                  width: 300,
                  border: `1px solid ${colors.border}`,
                }}
              >
                <Calendar
                  onChange={(date) => {
                    setSelectedDate(formatDate(date));
                    setShowCalendar(false);
                  }}
                  value={getCalendarValue()}
                  maxDate={new Date()}
                  className="custom-calendar"
                  dayAriaLabelPrefix="Select date"
                  tileClassName={({ date, view }) =>
                    view === "month" && date.getDate() === 1
                      ? "highlight-tile"
                      : null
                  }
                  minDetail="month"
                  maxDetail="month"
                  showNeighboringMonth={false}
                  style={{ border: "none" }}
                />
              </Box>
            </Popper> */}
          </Box>
          <CalendarPicker
            selectedDate={selectedDate}
            setSelectedDate={setSelectedDate}
            enableCalendar={enableCalendar}
            maxDate={new Date()}
          />
        </Box>

        {/* Main Section: Sidebar + Graph */}
        <Box
          sx={{
            display: "flex",
            flex: 1,
            flexDirection: "row",
            background: "transparent", // <-- Make sure this is transparent
          }}
        >
          {/* Sidebar */}
          <Box
            sx={{
              width: 160,
              background: colors.bgPaper, // This is fine, matches parent
              minHeight: 350,
              height: 350,
              display: "flex",
              flexDirection: "column",
              justifyContent: "space-between",
              p: 2,
              pt: 3,
              gap: 0,
              boxSizing: "border-box",
            }}
          >
            <SidebarCard
              icon={
                <img
                  src="/Bag.png"
                  alt="Total Orders"
                  style={{
                    color: "white",
                    fontSize: 20,
                    height: 25,
                    width: 25,
                  }}
                />
              }
              value={totalOrders}
              label="Total Orders"
              color="#007DFF"
              onClick={() => navigate(`/order-management`)}
            />
            <SidebarCard
              icon={<LocalShippingIcon sx={{ color: "white", fontSize: 20 }} />}
              value={totalDelivery}
              label="Total Delivery"
              color="#0C00EC"
              onClick={() => {
                localStorage.setItem(
                  "order-filter-data",
                  JSON.stringify({
                    filters: [
                      {
                        key: "status",
                        values: ["Delivered"],
                      },
                    ],
                  }),
                );
                navigate(`/order-management?child=true`);
              }}
            />
            <SidebarCard
              icon={<TotalInventory sx={{ color: "white", fontSize: 20 }} />}
              value={totalInventory}
              label="Total Inventory"
              color="#0D57D1"
            />
            <SidebarCard
              icon={
                <img
                  src="/Bag.png"
                  alt="Total Orders"
                  style={{
                    color: "white",
                    fontSize: 20,
                    height: 25,
                    width: 25,
                  }}
                />
              }
              onClick={() => {
                localStorage.setItem(
                  "order-filter-data",
                  JSON.stringify({
                    filters: [
                      {
                        key: "order_date",
                        values: {
                          //start of the month to end of the month
                          startDate: "2025-07-01",
                          endDate: "2025-07-31",
                          key: "selection",
                        },
                      },
                    ],
                  }),
                );
                navigate(`/order-management?child=true`);
              }}
              value={newOrders}
              label="New Order"
              color="#003F7F"
            />
          </Box>
          {/* Graph Section */}
          <Box
            sx={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "center",
              height: 350,
              minHeight: 350,
              maxHeight: 350,
              background: colors.bgTertiary,
              p: 0, // Remove padding to allow full width/height
            }}
          >
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={
                  orderRangeStats.intervals?.length > 0
                    ? orderRangeStats.intervals.map((x) => ({
                        date: x.label,
                        value: x.count,
                      }))
                    : []
                }
                margin={{ left: 0, right: 24, top: 24, bottom: 0 }} // Remove left padding
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
                <XAxis
                  dataKey="date"
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#666" }}
                  padding={{ left: 0, right: 0 }} // Remove XAxis padding
                />
                <YAxis
                  axisLine={false}
                  tickLine={false}
                  tick={{ fontSize: 10, fill: "#666" }}
                  tickFormatter={(value) => `${value / 1000}K`}
                  padding={{ left: 0, right: 0 }} // Remove YAxis padding
                />
                <Area
                  type="monotone"
                  dataKey="value"
                  stroke="#3BBDC4"
                  fill="#3BBDC4"
                  fillOpacity={0.18}
                />
                <Tooltip
                  content={({ active, payload, label }) => {
                    if (active && payload && payload.length) {
                      return (
                        <Box
                          sx={{
                            backgroundColor: "#333",
                            color: "white",
                            p: 2,
                            borderRadius: 1,
                            fontSize: 12,
                          }}
                        >
                          <CustomTypography
                            variant="body2"
                            sx={{ color: "white", fontSize: 12 }}
                          >
                            {`Total Orders: ${orderRangeStats.total.toLocaleString()}`}
                          </CustomTypography>
                          <CustomTypography
                            variant="body2"
                            sx={{ color: "white", fontSize: 12 }}
                          >
                            {`New Orders: ${
                              payload[0]?.payload?.value.toLocaleString() || 0
                            }`}
                          </CustomTypography>
                          <CustomTypography
                            variant="caption"
                            sx={{ color: "#ccc", fontSize: 10 }}
                          >
                            {label}
                          </CustomTypography>
                        </Box>
                      );
                    }
                    return null;
                  }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </Box>
        </Box>
      </Box>
    </Box>
  );
};

export default DashboardOrders;
